import { toEpochMs } from './scheduleUtils';

export function parseFiveYearCutoverDetails(response) {
  const details = response?.details;
  if (details == null || typeof details !== 'object') {
    return { status: 0, cutOverDt: null, fullEndDt: null, originalPlanQty: null, scheduledQty: null };
  }

  const status = Number(details.status);
  const scheduledQty = Number(details.scheduledQty);
  return {
    status: Number.isFinite(status) ? status : 0,
    cutOverDt: details.cutOverDt ?? null,
    fullEndDt: details.fullEndDt ?? null,
    originalPlanQty: details.originalPlanQty ?? null,
    scheduledQty: Number.isFinite(scheduledQty) && scheduledQty > 0 ? scheduledQty : null
  };
}

export function isFiveYearCutoverPending(status) {
  return Number(status) === 1;
}

export function hasValidFiveYearCutoverEndDate(fullEndDt) {
  return toEpochMs(fullEndDt) != null;
}

export function shouldHideAutoRenewLinks({ loading, pendingCutover } = {}) {
  return Boolean(loading || pendingCutover);
}

export function formatFiveYearCutoverDate(cutOverDt, locale = 'en-US') {
  const ms = toEpochMs(cutOverDt);
  if (ms == null) return '';

  return new Date(ms).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  });
}

export function withFiveYearCutoverMarker(display) {
  if (display == null || display === '') return display;
  return `${display}*`;
}

export function resolvePlanExpirationDate({ pendingCutover, cutOverDt, fullEndDt, fallbackExpiration } = {}) {
  if (!pendingCutover) return fallbackExpiration;
  return toEpochMs(cutOverDt ?? fullEndDt) ?? fallbackExpiration;
}

export function resolveDisplayedPlanExpiration({ pendingCutover, cutOverDt, fullEndDt, fallbackExpiration, locale } = {}) {
  if (!pendingCutover) return fallbackExpiration;
  const formatted = formatFiveYearCutoverDate(cutOverDt ?? fullEndDt, locale);
  return formatted || fallbackExpiration;
}

export function resolveFiveYearCutoverExtensionTooltip({ pendingCutover, fullEndDt, locale, formatMessage } = {}) {
  if (!pendingCutover || fullEndDt == null) return null;
  const fullEndDate = formatFiveYearCutoverDate(fullEndDt, locale);
  if (!fullEndDate) return null;
  if (typeof formatMessage === 'function') {
    return formatMessage(
      {
        id: 'fiveYearCutoverExtensionTooltip',
        defaultMessage: 'Auto-extends to {fullEndDate} at no cost — no action needed.'
      },
      { fullEndDate }
    );
  }
  return `Auto-extends to ${fullEndDate} at no cost — no action needed.`;
}

export function utcDateKey(value) {
  const ms = toEpochMs(value);
  if (ms == null) return null;
  return new Date(ms).toISOString().slice(0, 10);
}

export function isDeviceLicExpImpactedByCutover({ licExpDate, pendingCutover, cutOverDt, fullEndDt } = {}) {
  return Boolean(
    pendingCutover && toEpochMs(fullEndDt) != null && utcDateKey(licExpDate) != null && utcDateKey(licExpDate) === utcDateKey(cutOverDt)
  );
}

export function resolveDeviceDisplayedLicExpEpoch({ licExpDate, pendingCutover, cutOverDt, fullEndDt } = {}) {
  if (isDeviceLicExpImpactedByCutover({ licExpDate, pendingCutover, cutOverDt, fullEndDt })) {
    return toEpochMs(cutOverDt);
  }
  return licExpDate;
}

export function formatDeviceDisplayedLicExpDate({ licExpDate, pendingCutover, cutOverDt, fullEndDt, formatDate } = {}) {
  const displayedEpoch = resolveDeviceDisplayedLicExpEpoch({
    licExpDate,
    pendingCutover,
    cutOverDt,
    fullEndDt
  });
  const formatted = formatDate(displayedEpoch);
  if (!formatted || formatted === 'N/A') return formatted;
  if (isDeviceLicExpImpactedByCutover({ licExpDate, pendingCutover, cutOverDt, fullEndDt })) {
    return withFiveYearCutoverMarker(formatted);
  }
  return formatted;
}
